import { beginProductAction, beginProductRequest, classifyProductFailure } from '../lib/clientAnalytics';

type ProductAction = Parameters<typeof beginProductAction>[0];
type ProductProperties = Parameters<typeof beginProductAction>[1];

/**
 * Measures a submitted operation, from API invocation to the server's answer.
 * This duration is request time, not time spent filling in a form. Callers pass
 * only fixed categories; bodies, URLs and records never enter analytics.
 * Keep the original response/rejection so analytics cannot change the UI flow.
 */
export async function trackProductRequest<T extends { data?: unknown }>(
  action: ProductAction,
  properties: ProductProperties,
  request: () => Promise<T>,
): Promise<T> {
  const attempt = beginProductAction(action, properties);
  try {
    const response = await request();
    const body = response.data as { success?: unknown } | null | undefined;
    if (body?.success === true) {
      attempt.succeed();
    } else {
      // HTTP 200 alone does not prove that a business operation was accepted.
      attempt.fail(body?.success === false ? 'business_rule' : 'server');
    }
    return response;
  } catch (error) {
    const reason = classifyProductFailure(error);
    if (reason === 'cancelled') attempt.cancel('cancelled');
    else attempt.fail(reason);
    throw error;
  }
}

function statusClass(status: unknown): '2xx' | '3xx' | '4xx' | '5xx' | 'unknown' {
  if (typeof status !== 'number') return 'unknown';
  if (status >= 200 && status < 300) return '2xx';
  if (status >= 300 && status < 400) return '3xx';
  if (status >= 400 && status < 500) return '4xx';
  if (status >= 500 && status < 600) return '5xx';
  return 'unknown';
}

/** Only explicitly selected page-loading operations use this, never arbitrary URLs. */
export async function trackProductLoad<T extends { data?: unknown; status?: number }>(
  operation: Parameters<typeof beginProductRequest>[0],
  request: () => Promise<T>,
): Promise<T> {
  const measurement = beginProductRequest(operation);
  try {
    const response = await request();
    const body = response.data as { success?: unknown } | null | undefined;
    measurement.complete(body?.success === true ? 'success' : 'failure', {
      status_class: statusClass(response.status),
      // A successful HTTP request does not tell us whether server data was cached.
      cache_status: 'unknown',
    });
    return response;
  } catch (error) {
    const reason = classifyProductFailure(error);
    const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
    measurement.complete(reason === 'cancelled' ? 'cancelled' : 'failure', {
      status_class: reason === 'network' || reason === 'timeout' ? 'network' : statusClass(status),
      cache_status: 'unknown',
    });
    throw error;
  }
}
